import {
  Bike,
  LayoutDashboard,
  LogOut,
  Menu,
  ShoppingCart,
  User,
} from "lucide-react";
import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useHasPermission, useLogout } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

import {
  activityLogNav,
  financeNav,
  globalSettingsNav,
  inventoryNav,
  riderPayrollNav,
  riderPendingPaymentNav,
  riderStockNav,
  workspaceDataNav,
} from "../nav-config";
import { BottomNavGroup } from "./bottom-nav-menu";

import type { NavItem } from "../nav-config";

// Rider stock and payment screens live under "Keuangan" on mobile (there is no Penjualan group here).
const mobileFinanceNav: NavItem[] = [
  ...financeNav,
  riderStockNav,
  riderPendingPaymentNav,
  riderPayrollNav,
];
const mobileGlobalSettingsNav: NavItem[] = [
  ...globalSettingsNav,
  activityLogNav,
];
const accountNav: NavItem[] = [
  { to: "/settings/account", label: "Akun Saya", icon: User },
];

const moreActivePrefixes = [
  "/master",
  "/settings",
  "/receivables",
  "/expenses",
  "/cash-report",
  "/rider-pending-payment",
  "/rider-payrolls",
  "/stocks",
  "/stock-opname",
  "/activity-logs",
];

const tabClass = (active: boolean) =>
  cn(
    "flex h-full flex-1 flex-col items-center justify-center gap-0.5 rounded-full text-[10px] font-medium transition-colors",
    active
      ? "bg-muted text-foreground"
      : "text-muted-foreground hover:bg-muted",
  );

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = moreActivePrefixes.some((p) =>
    location.pathname.startsWith(p),
  );
  const logout = useLogout();
  const canSales = useHasPermission("sales.view");
  const canRiderSales = useHasPermission("rider_sales.view");
  const close = () => setMoreOpen(false);

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <>
      <nav
        style={{ bottom: "calc(2rem + env(safe-area-inset-bottom))" }}
        className="fixed inset-x-4 z-50 flex h-16 items-center justify-around rounded-full border bg-background p-1.5 shadow-lg"
      >
        <NavLink
          to="/dashboard"
          className={({ isActive }) => tabClass(isActive)}
        >
          <LayoutDashboard className="h-5 w-5" />
          Dashboard
        </NavLink>
        {canSales && (
          <NavLink to="/sales" className={({ isActive }) => tabClass(isActive)}>
            <ShoppingCart className="h-5 w-5" />
            Penjualan
          </NavLink>
        )}
        {canRiderSales && (
          <NavLink
            to="/rider-sales"
            className={({ isActive }) => tabClass(isActive)}
          >
            <Bike className="h-5 w-5" />
            Rider
          </NavLink>
        )}
        <button
          onClick={() => setMoreOpen(true)}
          className={tabClass(moreActive)}
        >
          <Menu className="h-5 w-5" />
          Lainnya
        </button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[80vh] pb-safe">
          <SheetHeader className="mb-2">
            <SheetTitle>Menu Lainnya</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 overflow-y-auto pb-4">
            <BottomNavGroup
              title="Keuangan"
              items={mobileFinanceNav}
              onNavigate={close}
            />
            <BottomNavGroup
              title="Inventaris"
              items={inventoryNav}
              onNavigate={close}
            />
            <BottomNavGroup
              title="Master Data & Pengaturan Toko"
              items={workspaceDataNav}
              onNavigate={close}
            />
            <BottomNavGroup
              title="Pengaturan Global"
              items={mobileGlobalSettingsNav}
              onNavigate={close}
            />
            <BottomNavGroup
              title="Akun"
              items={accountNav}
              onNavigate={close}
            />
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground hover:bg-muted"
            >
              <LogOut className="h-4 w-4" />
              Keluar
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
